import React, { useState, useEffect } from "react";
import { Constants } from "@eagerdog/constants";
import { IDog, IDogRun } from "@eagerdog/interfaces";

import "./RallyRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun | null;
}

const RALLY_CLASSES = [
  { value: "Novice A", label: "Novice A" },
  { value: "Novice B", label: "Novice B" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced A", label: "Advanced A" },
  { value: "Advanced B", label: "Advanced B" },
  { value: "Excellent A", label: "Excellent A" },
  { value: "Excellent B", label: "Excellent B" },
  { value: "Master", label: "Master" }
];

const RallyRunForm: React.FC<IProps> = ({
  dog,
  onSubmit,
  onCancel,
  isSubmitting,
  editRun
}) => {
  const [rallyClass, setRallyClass] = useState("");
  const [score, setScore] = useState("");
  const [resultStatus, setResultStatus] = useState("");
  const [trialDate, setTrialDate] = useState("");
  const [judgeNumber, setJudgeNumber] = useState("");
  const [judgeName, setJudgeName] = useState("");
  const [eventName, setEventName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editRun) {
      setRallyClass(editRun.class || "");
      setScore(editRun.score !== undefined ? String(editRun.score) : "");
      setResultStatus(editRun.result_status || "");
      setTrialDate(editRun.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : "");
      setJudgeNumber(editRun.judge_number || "");
      setJudgeName(editRun.judge_name || "");
      setEventName(editRun.event_name || "");
      setCity(editRun.location?.city || "");
      setState(editRun.location?.state || "");
      setNotes(editRun.notes || "");
    }
  }, [editRun]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);

    const formData: any = {
      dogId: dog._id,
      rallyClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: city || state ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      formData.run_id = editRun._id;
    }

    await onSubmit(formData);
  };

  return (
    <form className="rallyRunForm" onSubmit={handleSubmit}>
      <h2>{editRun ? 'Edit' : 'Add'} AKC Rally Run</h2>
      <p className="dogName">For: {dog.call_name}</p>

      <div className="formGrid">
        {/* Rally Class */}
        <div className="formGroup">
          <label htmlFor="rallyClass">
            Class <span className="required">*</span>
          </label>
          <select
            id="rallyClass"
            value={rallyClass}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRallyClass(e.target.value)}
            required
          >
            <option value="">Select class...</option>
            {RALLY_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Score */}
        <div className="formGroup">
          <label htmlFor="score">
            Score <span className="required">*</span> (0-100)
          </label>
          <input
            id="score"
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={score}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScore(e.target.value)}
            placeholder="e.g., 85.5"
            required
          />
          <span className="fieldHelp">Half points allowed (e.g., 85.5)</span>
        </div>

        {/* Result Status */}
        <div className="formGroup">
          <label htmlFor="resultStatus">
            Result <span className="required">*</span>
          </label>
          <select
            id="resultStatus"
            value={resultStatus}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setResultStatus(e.target.value)}
            required
          >
            <option value="">Select result...</option>
            <option value={Constants.run_result_status.qualified}>Qualified</option>
            <option value={Constants.run_result_status.not_qualified}>Not Qualified</option>
          </select>
        </div>

        {/* Trial Date */}
        <div className="formGroup">
          <label htmlFor="trialDate">
            Trial Date <span className="required">*</span>
          </label>
          <input
            id="trialDate"
            type="date"
            value={trialDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrialDate(e.target.value)}
            required
          />
        </div>

        {/* Judge Number */}
        <div className="formGroup">
          <label htmlFor="judgeNumber">
            Judge Number
          </label>
          <input
            id="judgeNumber"
            type="text"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="e.g., 12345"
          />
        </div>

        {/* Judge Name */}
        <div className="formGroup">
          <label htmlFor="judgeName">
            Judge Name
          </label>
          <input
            id="judgeName"
            type="text"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="e.g., Jane Smith"
          />
        </div>

        {/* Event Name */}
        <div className="formGroup fullWidth">
          <label htmlFor="eventName">
            Event Name
          </label>
          <input
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="e.g., Spring Rally Trial"
          />
        </div>

        {/* City */}
        <div className="formGroup">
          <label htmlFor="city">
            City
          </label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="e.g., Portland"
          />
        </div>

        {/* State */}
        <div className="formGroup">
          <label htmlFor="state">
            State
          </label>
          <input
            id="state"
            type="text"
            value={state}
            maxLength={2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value.toUpperCase())}
            placeholder="CA"
          />
        </div>

        {/* Notes (full width) */}
        <div className="formGroup fullWidth">
          <label htmlFor="notes">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
            placeholder="Any additional notes..."
            rows={3}
          />
        </div>
      </div>

      <div className="formActions">
        <button
          type="button"
          className="cancelButton"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="submitButton"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : (editRun ? "Update Run" : "Add Run")}
        </button>
      </div>
    </form>
  );
};

export default RallyRunForm;

